/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { ToastContainer, toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import ShareLinkStyles, { themeShareLinkStyles } from './sharelink.style';

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    link: string;
    linkName: string;
    iconName: string;
    ariaLabel: string;
    showTooltip: boolean;
    toolTipText: string;
    fundUrlMapping: {
        parameters: {
            key: string;
            value: string;
        }[];
        fundUrlTag: string;
    }[];
    toasterPosition:
        | 'top-right'
        | 'top-center'
        | 'top-left'
        | 'bottom-right'
        | 'bottom-center'
        | 'bottom-left';
    typographyClass?: string;
}

const ShareLink = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        link,
        linkName,
        iconName,
        showTooltip,
        toolTipText,
        fundUrlMapping,
        toasterPosition,
        ariaLabel,
    } = widgetProperties || props;

    const i18n = commonHooks.useLocalization();

    const location = useLocation();
    const search = location.search;
    const params = new URLSearchParams(search);

    const data: any = props.data;
    const theme = data?.themeSelected || (props as commonTypes.WidgetInstanceProps).theme;
    const toolTipTextCompiled = i18n.format(toolTipText, data);
    const ariaLabelCompiled = i18n.format(ariaLabel, data);

    const copy = () => {
        let copiedLink = link || window.location.href;

        if (fundUrlMapping) {
            fundUrlMapping.forEach((u) => {
                const parametersToCheck = u.parameters;

                const doParamsMatchCondition = parametersToCheck.every((p) => {
                    const paramValue = params.get(p.key) || null;
                    return paramValue === p.value;
                });

                if (doParamsMatchCondition) {
                    const mappedFundUrl = (data?.relatedDocuments || []).find(
                        (d: any) => d.tags === u.fundUrlTag
                    );

                    if (mappedFundUrl && mappedFundUrl.url) {
                        copiedLink = mappedFundUrl.url;
                    }
                }
            });
        }

        const el = document.createElement('input');
        el.value = copiedLink;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
    };

    const showToast = () => toast(i18n.t('widgets_shareLink_linkCopied'), { type: 'success' });

    const handleShareClick = () => {
        copy();
        !link && showToast();
    };

    return (
        <div
            className={`share-link-widget ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            css={css`
                ${!theme ? ShareLinkStyles : themeShareLinkStyles}
                ${ShareLinkStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
            data-testid="share-link-widget"
        >
            <button
                onClick={handleShareClick}
                tabIndex={0}
                aria-label={ariaLabelCompiled}
                className={`sharelink ${showTooltip ? 'link-tooltip' : ''}`}
                title={linkName}
                data-tooltip-text={toolTipTextCompiled}
            >
                <i className={`${iconName}`}></i>
            </button>

            <ToastContainer
                position={toasterPosition || 'top-center'}
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
            />
        </div>
    );
};

export default ShareLink;
